/**
 * TextureCatalog.ts
 * Maps themes, cell types, and wall states to tileable texture file paths.
 * Supports multiple variants per entry — renderer picks one randomly per cell.
 *
 * All paths relative to /textures/ (Vite serves from public/).
 */

import type { CellType, CellTheme, WallState } from '../types/OctileTypes.ts'

// ─── Variant Paths ───────────────────────────────────────────

/** Array of texture paths; renderer picks a random variant per cell */
type TextureVariants = string[]

// ─── Floor Textures by Theme (fallback when cell type has no override) ──

export const THEME_FLOOR_TEXTURES: Record<CellTheme, TextureVariants> = {
    natural_cave:  ['textures/floors/cave_natural_001.png', 'textures/floors/cave_natural_002.png'],
    ancient_stone: ['textures/floors/cave_chamber_001.png'],
    crystal:       ['textures/floors/cave_crystal_001.png'],
    organic:       ['textures/floors/cave_mushroom_001.png', 'textures/floors/cave_mushroom_002.png', 'textures/floors/cave_mushroom_003.png'],
    frozen:        ['textures/floors/cave_ice_001.png', 'textures/floors/cave_ice_002.png', 'textures/floors/cave_ice_003.png'],
    volcanic:      ['textures/floors/cave_lava_001.png', 'textures/floors/cave_lava_002.png', 'textures/floors/cave_lava_003.png'],
    overgrown:     ['textures/floors/cave_mushroom_004.png', 'textures/floors/cave_mushroom_005.png', 'textures/floors/cave_mushroom_006.png'],
    shadow:        ['textures/floors/cave_orb_001.png', 'textures/floors/cave_orb_002.png', 'textures/floors/cave_orb_003.png'],
}

// ─── Floor Textures by Cell Type (overrides theme when present) ──

export const CELL_TYPE_FLOOR_TEXTURES: Partial<Record<CellType, TextureVariants>> = {
    // Natural
    cave:               ['textures/floors/cave_natural_001.png', 'textures/floors/cave_natural_002.png'],
    stalactite_cave:    ['textures/floors/cave_stalactite_001.png', 'textures/floors/cave_stalactite_002.png', 'textures/floors/cave_stalactite_003.png'],
    stalagmite_cave:    ['textures/floors/cave_stalactite_004.png', 'textures/floors/cave_stalactite_005.png'],
    crystal_cave:       ['textures/floors/cave_crystal_001.png'],
    mushroom_grotto:    ['textures/floors/cave_mushroom_001.png', 'textures/floors/cave_mushroom_002.png', 'textures/floors/cave_mushroom_003.png',
                         'textures/floors/cave_mushroom_004.png', 'textures/floors/cave_mushroom_005.png', 'textures/floors/cave_mushroom_006.png',
                         'textures/floors/cave_mushroom_007.png'],
    water_pool:         ['textures/floors/cave_water_001.png', 'textures/floors/cave_water_002.png', 'textures/floors/cave_water_003.png', 'textures/floors/cave_water_004.png'],
    underground_stream: ['textures/floors/cave_water_005.png', 'textures/floors/cave_water_006.png', 'textures/floors/cave_water_007.png', 'textures/floors/cave_water_008.png'],
    lava_cell:          ['textures/floors/cave_lava_001.png', 'textures/floors/cave_lava_002.png', 'textures/floors/cave_lava_003.png', 'textures/floors/cave_lava_004.png',
                         'textures/floors/cave_lava_005.png', 'textures/floors/cave_lava_006.png', 'textures/floors/cave_lava_007.png', 'textures/floors/cave_lava_008.png'],
    ice_cave:           ['textures/floors/cave_ice_001.png', 'textures/floors/cave_ice_002.png', 'textures/floors/cave_ice_003.png'],
    collapsed:          ['textures/floors/cave_stalactite_001.png'],

    // Sacred / Special
    entrance:           ['textures/floors/cave_mouth_001.png'],
    shrine:             ['textures/floors/cave_orb_004.png', 'textures/floors/cave_orb_005.png', 'textures/floors/cave_orb_006.png'],
    boss_throne:        ['textures/floors/cave_orb_007.png', 'textures/floors/cave_orb_008.png', 'textures/floors/cave_orb_009.png', 'textures/floors/cave_orb_010.png'],

    // Hazard
    trap_floor:         ['textures/floors/cave_acid_001.png', 'textures/floors/cave_acid_002.png', 'textures/floors/cave_acid_003.png'],
    trap_walls:         ['textures/floors/cave_acid_001.png'],
    gauntlet:           ['textures/floors/cave_acid_002.png', 'textures/floors/cave_acid_003.png'],

    // Constructed
    chamber:            ['textures/floors/cave_chamber_001.png'],
    pillared_hall:      ['textures/floors/cave_chamber_001.png'],

    // Treasure
    vault:              ['textures/floors/cave_orb_001.png', 'textures/floors/cave_orb_002.png'],
    cache:              ['textures/floors/cave_orb_003.png'],
    hoard:              ['textures/floors/cave_orb_004.png', 'textures/floors/cave_orb_005.png'],
}

// ─── Wall Textures by Theme (SOLID walls use these) ──────────

export const THEME_WALL_TEXTURES: Record<CellTheme, TextureVariants> = {
    natural_cave:  ['textures/walls/stone_solid_001.png', 'textures/walls/stone_solid_002.png', 'textures/walls/stone_solid_003.png'],
    ancient_stone: ['textures/walls/stone_runic_001.png', 'textures/walls/stone_runic_002.png', 'textures/walls/stone_runic_003.png', 'textures/walls/stone_runic_004.png'],
    crystal:       ['textures/walls/stone_crystal_001.png', 'textures/walls/stone_crystal_002.png', 'textures/walls/stone_crystal_003.png', 'textures/walls/stone_crystal_004.png'],
    organic:       ['textures/walls/stone_slime_001.png', 'textures/walls/stone_slime_002.png', 'textures/walls/stone_slime_003.png'],
    frozen:        ['textures/walls/stone_ice_001.png', 'textures/walls/stone_ice_002.png', 'textures/walls/stone_ice_003.png'],
    volcanic:      ['textures/walls/stone_lava_001.png', 'textures/walls/stone_lava_002.png', 'textures/walls/stone_lava_003.png', 'textures/walls/stone_lava_004.png'],
    overgrown:     ['textures/walls/stone_slime_001.png', 'textures/walls/stone_slime_002.png'],
    shadow:        ['textures/walls/stone_gothic_001.png'],
}

// ─── Wall Textures by WallState (overrides theme for non-SOLID) ──

export const WALL_STATE_TEXTURES: Partial<Record<WallState, TextureVariants>> = {
    DOOR:    ['textures/walls/stone_door_001.png', 'textures/walls/stone_door_002.png'],
    GATE:    ['textures/walls/stone_gate_001.png', 'textures/walls/stone_gate_002.png', 'textures/walls/stone_gate_003.png', 'textures/walls/stone_gate_004.png'],
    GRATE:   ['textures/walls/stone_vent_001.png'],
    WINDOW:  ['textures/walls/stone_vent_001.png'],
    SECRET:  ['textures/walls/stone_solid_004.png', 'textures/walls/stone_solid_005.png'],
    // SOLID uses theme default, OPEN is near-invisible (no texture needed)
}

// ─── Special Wall Textures by Cell Type ──────────────────────

export const CELL_TYPE_WALL_TEXTURES: Partial<Record<CellType, TextureVariants>> = {
    shrine:      ['textures/walls/stone_shrine_001.png'],
    collapsed:   ['textures/walls/stone_damaged_001.png'],
}

// ─── Lookup Helpers ──────────────────────────────────────────

/** Deterministic pseudo-random variant selection based on grid position */
function pickVariant(variants: TextureVariants, row: number, col: number): string {
    // Simple hash from position → stable across re-renders
    const hash = ((row * 7919) + (col * 6271)) & 0x7FFFFFFF
    return variants[hash % variants.length]
}

/**
 * Get floor texture path for a cell.
 * Priority: cell type override → theme default
 */
export function getFloorTexturePath(
    type: CellType,
    theme: CellTheme,
    row: number,
    col: number
): string {
    const typeVariants = CELL_TYPE_FLOOR_TEXTURES[type]
    if (typeVariants) return pickVariant(typeVariants, row, col)
    return pickVariant(THEME_FLOOR_TEXTURES[theme], row, col)
}

/**
 * Get wall texture path.
 * Priority: wall state override → cell type walls → theme default
 * Returns null for OPEN walls (near-invisible, no texture needed).
 */
export function getWallTexturePath(
    state: WallState,
    cellType: CellType,
    theme: CellTheme,
    row: number,
    col: number,
    wallIndex: number
): string | null {
    if (state === 'OPEN' || state === 'NONE') return null

    // State-specific texture (DOOR, GATE, etc.)
    const stateVariants = WALL_STATE_TEXTURES[state]
    if (stateVariants) return pickVariant(stateVariants, row, col + wallIndex)

    // Cell type wall override (shrine, collapsed)
    const typeVariants = CELL_TYPE_WALL_TEXTURES[cellType]
    if (typeVariants) return pickVariant(typeVariants, row, col + wallIndex)

    // Theme default for SOLID walls
    return pickVariant(THEME_WALL_TEXTURES[theme], row, col + wallIndex)
}
